import { navigate } from "@reach/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDisclosure } from "@chakra-ui/react";
import { Documentation } from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Documentation";
import { ProcurerQuoteTab } from "./components/ProcurerQuoteTab";
import { PreviewRFQ } from "../../../../pages/RFQ/RFQ/CreateRFQ/PreviewRFQ";
import {
	formValuesGenerator,
} from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/createRFQUtils";
import { useSelector } from "react-redux";
import { TemplateDetail } from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/_components/TemplateDetail";
import {
	createTemplate,
	getTemplateById,
	saveRFXTemplate,
} from "actions/RFQ/templateAction";
import { logErrorIfDebug } from "utils/logging";
import { replaceNewInUrlWithId } from "helpers/linkHelper";
import ReturnableSchedule from "./components/ReturnableSchedule/ReturnableSchedule";
import { processFormValuesForSaving } from "./data-processor";
import RfxCreationLayout from "./components/RfxCreationLayout";
import { useRfxSubmissionData } from "../data-provider/RfxSubmissionDataProvider";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { useRfxCreationForm } from "./hooks/useRfxCreationForm";
import { showToast } from "utils/helpers";

const calculateCompletedSteps = (formValues, isQuoteTabValid) => {
	return [
		formValues?.template_name !== "" ? 1 : null,
		formValues?.documents?.length > 0 ? 2 : null,
		formValues?.sections?.filter((val) => val?.name !== "").length > 0
			? 3
			: formValues?.returnable_schedule_file
				? 3
				: null,
		isQuoteTabValid && 4
	].filter((value) => value !== null);
};

const CreateRfxTemplate = ({ isEdit, rfxTemplateRouteId: urlTemplateId }) => {
	const { rfxData, updateLocalRfxData } = useRfxSubmissionData();
	const [templateId, setTemplateId] = useState(urlTemplateId || null);
	const [hasTemplateLoaded, sethasTemplateLoaded] = useState(false);
	const { company_id } = useSelector((state) => state?.auth?.user);
	const { clientPaths } = useAuthContext();

	const formikRefLocal = useRef();

	const onFormInitialize = useCallback((formValues) => {
		if (isEdit) {
			formikRefLocal?.current?.setValues(formValues);
		}
		return true;
	}, [isEdit]);

	const {
		selectedTab,
		formikRef,
		isSubmittingForm,
		setIsSubmittingForm,
		isValidForm,
		setIsValidForm,
		rfxTypes,
		documentCategories,
		isNotFound,
		completedSteps,
		touchedTabs,
		setTouchedTabs,
		openModal,
		setOpenModal,
		tabRef,
		staticOptionsLoaded,
		isFileUploading,
		tabButtonList,
		activeTab,
		lastStep,
		enableOnChangeFormik,
		changeTab,
		onBackButtonClicked,
		resetRfxData,
		makeHandleTabChangeWithSave,
		makeHandleSubmitOutside,
		checkIsCurrentStepValid,
	} = useRfxCreationForm({
		isTemplate: true,
		lastStepTab: 4,
		forceSaveTabs: [2],
		rfxData,
		updateLocalRfxData,
		companyId: company_id,
		calculateCompletedSteps,
		onFormInitialize,
	});

	// Sync local ref
	formikRefLocal.current = formikRef.current;

	// ---- RFQ-specific: quoteTabRef for saving/validating quote tab before preview and during save progress ----
	const quoteTabRef = useRef(null);
	const { onOpen: onPreviewOpen, onClose, isOpen } = useDisclosure();
	const onOpen = useCallback(async () => {
		await quoteTabRef.current?.saveQuoteTab?.();
		onPreviewOpen();
	}, [onPreviewOpen]);
	const {
		onOpen: onApprovalOpen,
		onClose: onApprovalClose,
		isOpen: isApprovalOpen,
	} = useDisclosure();

	// ---- Template-specific: fetch template data ----
	const fetchTemplateDataOnce = useCallback(async () => {
		if (templateId === "new") {
			return;
		}
		if (templateId && !rfxData) {
			const fetchedTemplateData = await getTemplateById(templateId);
			if (fetchedTemplateData) {
				const templateData = {
					...fetchedTemplateData.data,
					...fetchedTemplateData.quoteData,
					id: fetchedTemplateData.id,
					name: fetchedTemplateData.name,
					companyId: fetchedTemplateData.companyId,
				};
				updateLocalRfxData(templateData);
				const formValues = formValuesGenerator(templateData, true);
				formikRef?.current?.setValues(formValues);
			}
		}
		sethasTemplateLoaded(true);
	}, [templateId, rfxData, updateLocalRfxData, formikRef]);

	useEffect(() => {
		sethasTemplateLoaded(false);
		fetchTemplateDataOnce().finally(sethasTemplateLoaded(true));
	}, [templateId, fetchTemplateDataOnce]);

	// ---- Template-specific: save handler ----
	const handleSave = useCallback(
		async (additional_message, type) => {
			const processContinueButton = (type) => {
				switch (type) {
					case "next":
						const currentIndex = tabButtonList.findIndex(
							(item) => item.value === selectedTab
						);
						const nextIndex = currentIndex + 1;
						if (tabButtonList?.length > nextIndex) {
							changeTab(tabButtonList[nextIndex]);
						}
						break;
					case "submit":
						navigate(clientPaths.rfx.manageTemplate);
						break;
					default:
						break;
				}
			};

			setIsSubmittingForm(true);

			// Quote tab has own save mechanism
			if (selectedTab === 4) {
				const saveResult = await quoteTabRef.current?.saveQuoteTab?.();
				if (saveResult) {
					showToast("Quote progress saved successfully", "Save Successful", true);
					processContinueButton(type);
				}
				setIsSubmittingForm(false);
				return saveResult;
			}

			try {
				const values = formikRef?.current?.values;
				const document_ids =
					values?.documents
						?.filter((doc) => !doc.isRemove)
						.map((doc) => doc.id)
						.filter(Boolean) || [];

				const template = {
					template_id: templateId,
					template_name: values?.template_name || values?.name || "New Template",
					document_ids: document_ids,
				};

				const submissionFormat = processFormValuesForSaving(
					additional_message,
					template,
					values
				);

				Object.keys(submissionFormat).forEach((key) => {
					if (submissionFormat[key] === undefined) {
						delete submissionFormat[key];
					}
				});

				const result =
					templateId === "new"
						? await createTemplate(values.template_name)
						: await saveRFXTemplate(
							submissionFormat,
							selectedTab,
							lastStep && openModal
						);

				if (result) {
					if (templateId === "new" || (!templateId && result.id)) {
						setTemplateId(result.id);
						replaceNewInUrlWithId(result.id);
					}

					const templateData = {
						...result.data,
						...result.quoteData,
						id: result.id,
						name: result.name,
						companyId: result.companyId,
						returnableScheduleFileId: result.data.returnableScheduleFile?.id,
					};
					updateLocalRfxData(templateData);
					const formValues = formValuesGenerator(templateData, true);
					formikRef?.current?.setValues(formValues);
					formikRef?.current?.submitForm();
					processContinueButton(type);
				} else {
					formikRef?.current?.submitForm();
					lastStep && openModal && setTouchedTabs([1, 2, 3, 4]);
					setOpenModal(false);
				}
				return true;
			} catch (e) {
				logErrorIfDebug("Error during handleSave:", e);
				setIsSubmittingForm(false);
				return false;
			} finally {
				setIsSubmittingForm(false);
			}
		},
		[
			lastStep,
			openModal,
			selectedTab,
			templateId,
			tabButtonList,
			updateLocalRfxData,
			clientPaths,
			formikRef,
			changeTab,
			setIsSubmittingForm,
			setTouchedTabs,
			setOpenModal,
		]
	);

	// Wire page-specific handleSave into the shared hook's factory functions
	const handleTabChangeWithSave = useMemo(
		() => makeHandleTabChangeWithSave(handleSave),
		[makeHandleTabChangeWithSave, handleSave]
	);

	const handleSubmitOutside = useMemo(
		() => makeHandleSubmitOutside(
			handleSave,
			(_type, additional_message) => {
				tabRef?.current?.scrollIntoView();
				checkIsCurrentStepValid(handleSave, additional_message, "submit");
			}
		),
		[makeHandleSubmitOutside, handleSave, checkIsCurrentStepValid, tabRef]
	);

	const { values: formikValues } = formikRef.current || {};
	// The validity term is what makes this a gate: without it the expression is
	// unconditionally true on the last step and blocks every form-type template
	// regardless of state. Do not remove it. `isQuoteTabValid` is the same
	// server-computed signal the RFx page gates on.
	const isTemplateQuoteFormInvalid =
		lastStep &&
		formikValues?.quote_type === "form" &&
		!rfxData?.isQuoteTabValid;

	const hasLoaded = hasTemplateLoaded && staticOptionsLoaded;

	const blockSendingRFx = lastStep
		? !isValidForm || isSubmittingForm || isTemplateQuoteFormInvalid || isFileUploading
		: isSubmittingForm || isFileUploading;

	return (
		<RfxCreationLayout
			isNotFound={isNotFound}
			selectedTab={selectedTab}
			formikRef={formikRef}
			isSubmittingForm={isSubmittingForm}
			isValidForm={isValidForm}
			setIsValidForm={setIsValidForm}
			completedSteps={completedSteps}
			touchedTabs={touchedTabs}
			tabRef={tabRef}
			isFileUploading={isFileUploading}
			tabButtonList={tabButtonList}
			lastStep={lastStep}
			enableOnChangeFormik={enableOnChangeFormik}
			onBackButtonClicked={onBackButtonClicked}
			handleTabChangeWithSave={handleTabChangeWithSave}
			handleSubmitOutside={handleSubmitOutside}
			isTemplate={true}
			isEdit={isEdit}
			hasLoaded={hasLoaded}
			initialBlockSendingRFx={blockSendingRFx}
			rfxData={rfxData}
			companyId={company_id}
			headerProps={{
				isTemplateQuoteFormInvalid: isTemplateQuoteFormInvalid,
				loadRFQData: !hasLoaded,
				onOpen: onOpen,
				onOpenApproval: onApprovalOpen,
			}}
			renderTabContent={({ setFieldError, setFieldValue }) => (
				<>
					{selectedTab === 1 && (
						<TemplateDetail
							titlePage={activeTab.title}
							subTitlePage={activeTab.subTitle}
							data={null}
							isEdit={isEdit}
						/>
					)}
					{selectedTab === 2 && (
						<Documentation
							titlePage={activeTab.title}
							subTitlePage={activeTab.subTitle}
							documentCategories={documentCategories}
							isTemplate={true}
							rfxOrRfxTemplateId={templateId}
						/>
					)}
					{selectedTab === 3 && (
						<ReturnableSchedule
							titlePage={activeTab.title}
							subTitlePage={activeTab.subTitle}
							setFieldError={setFieldError}
							setFieldValue={setFieldValue}
							isTemplate={true}
							rfxOrRfxTemplateId={templateId}
							previewRfxHandler={onOpen}
						/>
					)}
					{selectedTab === 4 && (
						<ProcurerQuoteTab
							ref={quoteTabRef}
							titlePage={activeTab.title}
							subTitlePage={activeTab.subTitle}
							resourceType="rfx-template"
							resourceId={templateId}
						/>
					)}
				</>
			)}
			renderAfterForm={() => (
				<>
					{isOpen && rfxData && (
						<PreviewRFQ
							isOpen={isOpen}
							onClose={onClose}
							rfqData={rfxData}
							documentCategories={documentCategories}
							rfqTypes={rfxTypes}
							isTemplate={true}
							previewTab={activeTab.previewTab}
						/>
					)}
				</>
			)}
		/>
	);
};

export default CreateRfxTemplate;
